import React, { useState, useEffect } from "react";
import { type NodeProps, type Node, useReactFlow } from "@xyflow/react";
import { Globe } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import HttpApiConfigModal, { KeyValueParam } from "./HttpApiConfigModal";
import { WorkflowBaseNode } from "../../../common/shared/WorkflowBaseNode";

export const HttpApiNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [method, setMethod] = useState<string>(data?.method || "GET");
  const [url, setUrl] = useState<string>(data?.url || "");
  const [authType, setAuthType] = useState<string>(data?.auth?.type || "none");
  const [authToken, setAuthToken] = useState<string>(data?.auth?.token || "");

  const initParams = (arr: any): KeyValueParam[] =>
    Array.isArray(arr) && arr.length > 0
      ? arr.map((item: any) => ({ id: uuidv4(), key: item.key || "", value: String(item.value || "") }))
      : [{ id: uuidv4(), key: "", value: "" }];

  const [sendQuery, setSendQuery] = useState<boolean>((data?.query?.length > 0) || false);
  const [queryParams, setQueryParams] = useState<KeyValueParam[]>(() => initParams(data?.query));
  const [sendHeaders, setSendHeaders] = useState<boolean>((data?.headers?.length > 0) || false);
  const [headers, setHeaders] = useState<KeyValueParam[]>(() => initParams(data?.headers));
  const [sendBody, setSendBody] = useState<boolean>((data?.body && (Array.isArray(data.body) ? data.body.length > 0 : true)) || false);
  const [isJsonBody, setIsJsonBody] = useState<boolean>(data?.is_json || false);
  const [jsonBody, setJsonBody] = useState<string>(
    data?.is_json && data?.body && !Array.isArray(data.body) ? JSON.stringify(data.body, null, 2) : "{\n  \n}",
  );
  const [bodyParams, setBodyParams] = useState<KeyValueParam[]>(() => initParams(data?.body));
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { setNodes } = useReactFlow();

  // Sync state to node data
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== id) return node;
        let bodyData: any = undefined;
        if (sendBody) {
          if (isJsonBody) {
            try { bodyData = JSON.parse(jsonBody); } catch { bodyData = jsonBody; }
          } else {
            const arr = bodyParams.filter((p) => p.key.trim()).map((p) => ({ key: p.key, value: p.value }));
            if (arr.length > 0) bodyData = arr;
          }
        }
        const queryArray = sendQuery ? queryParams.filter((p) => p.key.trim()).map((p) => ({ key: p.key, value: p.value })) : [];
        const headersArray = sendHeaders ? headers.filter((h) => h.key.trim()).map((h) => ({ key: h.key, value: h.value })) : [];
        const authObj = authType === "bearer" && authToken ? { type: "bearer", token: authToken } : authType !== "none" ? { type: authType } : null;
        return {
          ...node,
          data: {
            ...node.data, url, method, is_json: isJsonBody,
            ...(bodyData !== undefined && { body: bodyData }),
            ...(queryArray.length > 0 && { query: queryArray }),
            ...(headersArray.length > 0 && { headers: headersArray }),
            ...(authObj && { auth: authObj }),
          },
        };
      }),
    );
  }, [method, url, authType, authToken, id, setNodes, sendQuery, queryParams, sendHeaders, headers, sendBody, bodyParams, isJsonBody, jsonBody]);

  const addParam = (setter: React.Dispatch<React.SetStateAction<KeyValueParam[]>>) => {
    setter((prev) => [...prev, { id: uuidv4(), key: "", value: "" }]);
  };
  const removeParam = (paramId: string, setter: React.Dispatch<React.SetStateAction<KeyValueParam[]>>) => {
    setter((prev) => (prev.length <= 1 ? [{ id: uuidv4(), key: "", value: "" }] : prev.filter((item) => item.id !== paramId)));
  };
  const updateParam = (paramId: string, field: "key" | "value", newValue: string, setter: React.Dispatch<React.SetStateAction<KeyValueParam[]>>) => {
    setter((prev) => prev.map((item) => (item.id === paramId ? { ...item, [field]: newValue } : item)));
  };

  const isConfigured = !!url;

  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="HTTP Request"
      icon={<Globe className="w-4 h-4 text-indigo-600" />}
      nodeDocsType="HTTP-API"
      themeColor="indigo"
      configureLabel="Configure HTTP Request"
      isConfigured={isConfigured}
      hasSourceHandle={true}
      hasErrorHandle={true}
      validationErrors={[
        ...(!url ? ["URL is required"] : []),
        ...(authType === "bearer" && !authToken ? ["Bearer token is required"] : []),
      ]}
      statusText={{
        running: "Executing...",
        success: "Executed",
        error: "Execution Failed",
      }}
      onConfigure={() => setIsModalOpen(true)}
    >
      {/* Modal */}
      <HttpApiConfigModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        nodeId={id}
        method={method} setMethod={setMethod}
        url={url} setUrl={setUrl}
        authType={authType} setAuthType={setAuthType}
        authToken={authToken} setAuthToken={setAuthToken}
        sendQuery={sendQuery} setSendQuery={setSendQuery}
        queryParams={queryParams} setQueryParams={setQueryParams}
        sendHeaders={sendHeaders} setSendHeaders={setSendHeaders}
        headers={headers} setHeaders={setHeaders}
        sendBody={sendBody} setSendBody={setSendBody}
        isJsonBody={isJsonBody} setIsJsonBody={setIsJsonBody}
        jsonBody={jsonBody} setJsonBody={setJsonBody}
        bodyParams={bodyParams} setBodyParams={setBodyParams}
        executionStatus={data?.executionStatus || null}
        error={data?.error}
        outputs={data?.outputs}
        validationStatus={{ isValid: isConfigured, errors: [...(!url ? ["URL is required"] : []), ...(authType === "bearer" && !authToken ? ["Bearer token is required"] : [])] }}
        addParam={addParam} removeParam={removeParam} updateParam={updateParam}
      />
    </WorkflowBaseNode>
  );
};

export default HttpApiNode;
